import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errText } from "@/lib/api";

export const AssociateRecipients = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/associates");
      setList(data);
    } catch {
      setList([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!form.email.trim()) return toast.error("Enter the associate's email");
    try {
      await api.post("/associates", { name: form.name.trim() || null, email: form.email.trim() });
      toast.success("Associate added");
      setForm({ name: "", email: "" });
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/associates/${id}`);
      toast.success("Associate removed");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <section className="surface p-6 lg:p-8" data-testid="associates-section">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <Users className="h-5 w-5" /> Associates for the brief
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Saved here, they appear as tick boxes when you email the associate brief. You can always add
        a one-off address at send time.
      </p>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Input
          data-testid="associate-name-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Name (optional)"
          className="rounded-sm h-12 text-base sm:max-w-[14rem]"
        />
        <Input
          data-testid="associate-email-input"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="junior@chambers.com"
          className="rounded-sm h-12 text-base sm:max-w-sm"
        />
        <Button data-testid="add-associate-btn" onClick={add} className="rounded-full h-12 px-6">
          <UserPlus className="h-5 w-5 mr-2" /> Add
        </Button>
      </div>

      <div className="mt-6 divide-y divide-border">
        {list.map((r) => (
          <div
            key={r.recipient_id}
            data-testid={`associate-${r.recipient_id}`}
            className="py-4 flex items-center justify-between gap-4"
          >
            <div className="min-w-0">
              <p className="text-base font-semibold truncate">{r.name}</p>
              <p className="text-sm text-muted-foreground truncate">{r.email}</p>
            </div>
            <button
              data-testid={`remove-associate-${r.recipient_id}`}
              onClick={() => remove(r.recipient_id)}
              className="h-10 w-10 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {list.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No associates saved yet.</p>
        ) : null}
      </div>
    </section>
  );
};
